import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useContext } from 'react';
import type { UIElement } from '@adyen/adyen-web';
import type { AdyenApplePayHandlers } from '@/types/adyen';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import type { AdyenApplePayPaymentMethod } from '@/types/adyen';
import { adyenApplePayService } from '@/services/adyenApplePayService';
import type { AdyenFinalizePaymentResponse } from '@/types/adyen';
import type { ExpressCheckoutComponentProps } from '@/utils/expressPayment';
import { Errors } from '@/constants/errors';

export interface UseAdyenApplePayOptions extends ExpressCheckoutComponentProps {
  onClick?: AdyenApplePayHandlers['onClick'];
}

export function useAdyenApplePay({ onReady, onClick }: UseAdyenApplePayOptions = {}) {
  const {
    paymentMethodsSettings,
    pricing,
    customer,
    actions: { runPayment, setError },
  } = useContext(CheckoutContext);

  const [applePayElement, setApplePayElement] = useState<UIElement | null>(null);
  const [error, setErrorState] = useState<Error | null>(null);
  const isPaymentInProgress = useRef(false);
  const applePaymentDetailsRef = useRef<{ email: string; paymentMethodDetails: Record<string, unknown> } | null>(null);

  const applePayPaymentMethod = useMemo(
    () =>
      paymentMethodsSettings.find(
        (pm) => pm.paymentMethodName === PaymentMethodEnum.APPLE_PAY && pm.provider === ProviderEnum.ADYEN
      ) as AdyenApplePayPaymentMethod | undefined,
    [paymentMethodsSettings]
  );

  const handleError = useCallback(() => {
    // ACDEV-22923: uncomment when error handling is implemented
    // setErrorState(err instanceof Error ? err : new Error(Errors.payment.failedToInitializeApplePay));
    // setError?.(t('somethingWentWrong'));
    isPaymentInProgress.current = false;
  }, [setError]);

  useEffect(() => {
    if (!applePayPaymentMethod) {
      onReady?.(false);
      return;
    }
    let applePayInstance: UIElement | null = null;

    const initApplePay = async () => {
      try {
        await adyenApplePayService.initializeCheckoutInstance(applePayPaymentMethod, customer ?? undefined);

        const handlers: AdyenApplePayHandlers = {
          ...(onClick && { onClick }),
          onAuthorized: (data, actions) => {
            const applePayPayment = data.authorizedEvent.payment;
            applePaymentDetailsRef.current = {
              email: applePayPayment.shippingContact?.emailAddress || customer?.email || '',
              paymentMethodDetails: applePayPayment.token.paymentMethod as unknown as Record<string, unknown>,
            };
            actions.resolve();
          },
          onError: (err) => {
            console.error('Adyen Apple Pay onError:', err);
            handleError();
          },
          onPaymentFailed: () => {
            handleError();
          },
          onSubmit: async (state, _, actions) => {
            if (isPaymentInProgress.current) return;
            isPaymentInProgress.current = true;
            adyenApplePayService.setApplePayActions(actions);

            try {
              const details = applePaymentDetailsRef.current;
              if (!details) {
                handleError();
                return;
              }

              await runPayment<AdyenFinalizePaymentResponse>(
                {
                  paymentMethod: state.data.paymentMethod,
                  paymentMethodDetails: details.paymentMethodDetails,
                  email: details.email || customer?.email,
                },
                PaymentMethodEnum.APPLE_PAY,
                ProviderEnum.ADYEN
              );
            } catch (err) {
              handleError();
              adyenApplePayService.completeApplePayPayment(false);
            } finally {
              isPaymentInProgress.current = false;
            }
          },
        };

        const component = await adyenApplePayService.createApplePayComponent({
          applePayPaymentMethod,
          pricing,
          handlers,
        });

        if (!component) return;

        applePayInstance = component;
        setApplePayElement(applePayInstance);
        await component.isAvailable();
        onReady?.(true);
      } catch (err) {
        console.error('Adyen Apple Pay initialization error:', err);
        setErrorState(err instanceof Error ? err : new Error(Errors.payment.failedToInitializeApplePay));
        onReady?.(false);
      }
    };

    initApplePay();

    return () => {
      applePayInstance?.unmount();
    };
  }, [pricing]);

  return {
    applePayElement,
    error,
  };
}
